/**
 * Login Form Component
 *
 * Handles email/password login with comprehensive error handling and logging.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logger, LogCategory, logValidationError } from '@/utils/logger';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import { Button, Input } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { Mail, Lock, AlertCircle, ArrowLeft } from 'lucide-react';
import { isValidEmail } from '@/utils';
import { ROUTES } from '@/constants';

// ============================================================================
// Component
// ============================================================================

export function LoginForm() {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();

  const { login, loginWithGoogle, isLoading, error, clearError } = useAuthStore();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // Validate form
  const validateForm = (): boolean => {
    Logger.debug(LogCategory.VALIDATION, 'Validating login form', 'LoginForm');
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'Email is required';
      logValidationError('email', formData.email, 'required', 'Email is required', 'LoginForm');
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
      logValidationError('email', formData.email, 'format', 'Invalid email format', 'LoginForm');
    }

    if (!formData.password) {
      errors.password = 'Password is required';
      logValidationError('password', '***', 'required', 'Password is required', 'LoginForm');
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
      logValidationError('password', '***', 'minLength', 'Password too short', 'LoginForm');
    }

    setValidationErrors(errors);

    const isValid = Object.keys(errors).length === 0;
    Logger.info(LogCategory.VALIDATION, `Login form validation: ${isValid ? 'passed' : 'failed'}`, 'LoginForm', {
      errorCount: Object.keys(errors).length,
    });

    return isValid;
  };

  // Handle input change
  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    Logger.debug(LogCategory.UI, `Login form field changed: ${field}`, 'LoginForm');
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

    // Clear errors when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (error) {
      clearError();
    }
  };

  // Handle blur for validation
  const handleBlur = (field: string) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    Logger.info(LogCategory.AUTH, 'Login form submitted', 'LoginForm');

    // Mark all fields as touched
    setTouched({ email: true, password: true });

    if (!validateForm()) {
      Logger.warn(LogCategory.AUTH, 'Login form validation failed', 'LoginForm');
      return;
    }

    try {
      Logger.startTimer('loginAttempt');
      await login(formData.email, formData.password);
      Logger.endTimer('loginAttempt');

      // Check if login was successful
      const { isAuthenticated } = useAuthStore.getState();
      if (isAuthenticated) {
        Logger.info(LogCategory.AUTH, 'Login successful, navigating to dashboard', 'LoginForm');
        showSuccess('Welcome back!', 'You have been logged in successfully.');
        navigate(ROUTES.DASHBOARD);
      }
    } catch (err) {
      Logger.error(LogCategory.AUTH, 'Login form submission error', 'LoginForm', undefined, err);
      showError('Login failed', 'Please check your credentials and try again.');
    }
  };

  // Handle Google login
  const handleGoogleLogin = async () => {
    Logger.info(LogCategory.AUTH, 'Google login initiated', 'LoginForm');

    try {
      await loginWithGoogle();

      const { isAuthenticated } = useAuthStore.getState();
      if (isAuthenticated) {
        Logger.info(LogCategory.AUTH, 'Google login successful', 'LoginForm');
        showSuccess('Welcome!', 'You have been logged in with Google.');
        navigate(ROUTES.DASHBOARD);
      }
    } catch (err) {
      Logger.error(LogCategory.AUTH, 'Google login error', 'LoginForm', undefined, err);
      showError('Google login failed', 'Please try again.');
    }
  };

  // Handle forgot password submission
  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    Logger.info(LogCategory.AUTH, 'Forgot password form submitted', 'LoginForm');

    if (!forgotEmail) {
      showError('Email required', 'Please enter your email address.');
      return;
    }
    if (!isValidEmail(forgotEmail)) {
      showError('Invalid email', 'Please enter a valid email address.');
      return;
    }

    setForgotLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: window.location.origin + '/auth/callback',
      });

      if (resetError) {
        Logger.error(LogCategory.AUTH, 'Password reset request failed', 'LoginForm', undefined, resetError);
        showError('Reset failed', resetError.message);
      } else {
        Logger.info(LogCategory.AUTH, 'Password reset email sent', 'LoginForm');
        showSuccess('Check your email for a password reset link');
      }
    } catch (err) {
      Logger.error(LogCategory.AUTH, 'Password reset error', 'LoginForm', undefined, err);
      showError('Reset failed', 'An unexpected error occurred. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Handle WorkOS SSO
  const handleWorkOSLogin = () => {
    Logger.info(LogCategory.AUTH, 'WorkOS SSO initiated', 'LoginForm');
    // In a real app, this would redirect to WorkOS
    // For MVP, we'll simulate by calling loginWithWorkOS with a mock code
    showError('SSO Configuration Required', 'Please contact your administrator to configure SSO.');
  };

  return (
    <div className="w-full max-w-md">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Welcome back</h1>
        <p className="mt-2 text-slate-600">
          Sign in to your account to continue
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-danger-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-danger-800">Login failed</p>
            <p className="text-sm text-danger-700">{error}</p>
          </div>
        </div>
      )}

      {/* OAuth Buttons */}
      <div className="space-y-3 mb-6">
        <Button
          variant="outline"
          fullWidth
          onClick={handleGoogleLogin}
          disabled={isLoading}
          leftIcon={
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          }
        >
          Continue with Google
        </Button>

        <Button
          variant="outline"
          fullWidth
          onClick={handleWorkOSLogin}
          disabled={isLoading}
          leftIcon={
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
            </svg>
          }
        >
          Continue with SSO
        </Button>
      </div>

      {/* Divider */}
      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-slate-500">or continue with email</span>
        </div>
      </div>

      {showForgotPassword ? (
        /* Forgot Password Form */
        <form onSubmit={handleForgotPassword} className="space-y-4" noValidate>
          <p className="text-sm text-slate-600 mb-4">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <Input
            label="Email address"
            type="email"
            placeholder="you@company.com"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            leftIcon={<Mail className="h-4 w-4" />}
            autoComplete="email"
            required
            disabled={forgotLoading}
          />

          <Button
            type="submit"
            fullWidth
            isLoading={forgotLoading}
            className="mt-4"
          >
            Send Reset Link
          </Button>

          <button
            type="button"
            onClick={() => {
              setShowForgotPassword(false);
              setForgotEmail('');
            }}
            className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 mt-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </button>
        </form>
      ) : (
        /* Login Form */
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Input
            label="Email address"
            type="email"
            placeholder="you@company.com"
            value={formData.email}
            onChange={handleChange('email')}
            onBlur={handleBlur('email')}
            error={touched.email ? validationErrors.email : undefined}
            leftIcon={<Mail className="h-4 w-4" />}
            autoComplete="email"
            required
            disabled={isLoading}
          />

          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleChange('password')}
            onBlur={handleBlur('password')}
            error={touched.password ? validationErrors.password : undefined}
            leftIcon={<Lock className="h-4 w-4" />}
            autoComplete="current-password"
            required
            disabled={isLoading}
          />

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-slate-600">Remember me</span>
            </label>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setForgotEmail(formData.email);
              }}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Forgot password?
            </button>
          </div>

          <Button
            type="submit"
            fullWidth
            isLoading={isLoading}
            className="mt-6"
          >
            Sign in
          </Button>
        </form>
      )}

    </div>
  );
}
